import {
  Box,
  MenuItem,
  Typography,
  Paper,
  CircularProgress,
  Stack,
  Chip,
  Skeleton,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
} from "@mui/material";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../../store/Index";
import CustomSelect from "../../../../../components/common/Select";
import Button from "../../../../../components/common/Button";
import apiService from "../../../../../service/Api";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useCallback, useEffect, useRef, useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { showErrorToast } from "../../../../../components/common/ShowToast";
import PropTypes from "prop-types";

type CustomSelectChangeEvent =
  | React.ChangeEvent<HTMLInputElement>
  | (Event & { target: { value: unknown; name: string } });

interface Image {
  id: number;
  content_type: string;
  created_at: Date;
  deceased: number;
  description: string;
  file: string;
  title: string;
  updated_at: Date;
  status: string;
}

interface MemoryTableGalleryProps {
  onRefreshCount?: () => void;
}

export const MemoryTableGallery: React.FC<MemoryTableGalleryProps> = ({
  onRefreshCount,
}) => {
  const { id } = useParams<{ id: string }>();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [fetchedImages, setFetchedImages] = useState<Image[]>([]);
  const [renderingImages, setRenderingImages] = useState<Image[]>([]);
  const [checkedImagesIDs, setCheckedImagesIDs] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const bulkSelectAction = useRef<string>("approve");

  const fetchImages = useCallback(async () => {
    setIsLoading(true);
    setError(false);
    try {
      const response = await apiService.get<{ results: Image[] }>(
        `/api/individuals/deceased-gallery/?stream_id=${id}`,
      );

      const images: Image[] = response.data.results.filter(
        (image: Image) => image.content_type === "image",
      );

      setRenderingImages([...images]);
      setFetchedImages([...images]);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load images. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const addImageToChecked = (id: number) => {
    setCheckedImagesIDs((prev) => [...prev, id]);
  };

  const removeImageToChecked = (id: number) => {
    setCheckedImagesIDs((prev) => prev.filter((imageID) => imageID !== id));
  };

  const toggleChecked = () => {
    if (checkIsChecked()) {
      setCheckedImagesIDs([]);
    } else {
      setCheckedImagesIDs(renderingImages.map((image) => image.id));
    }
  };

  const checkIsChecked = () => {
    const renderingLength = renderingImages.length;
    const checkedLength = checkedImagesIDs.length;
    if (renderingLength === 0 && checkedLength === 0) return false;
    return renderingLength === checkedLength;
  };

  const updateBulkAction = (event: CustomSelectChangeEvent) => {
    bulkSelectAction.current = String(event.target.value);
  };

  // ─── Bulk action ────────────────────────────────────────────────────────────
  const bulkAction = async () => {
    const action = bulkSelectAction.current;
    if (checkedImagesIDs.length === 0) {
      toast.warning("Please select images for bulk action", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      return;
    }
    try {
      await apiService.post(
        `/api/individuals/deceased-gallery/bulk-${action}/`,
        { files_ids: checkedImagesIDs },
      );
      toast.success(`Images ${action}d`, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      await fetchImages();          // <-- wait for refresh
      onRefreshCount?.();           // <-- refresh badge count
    } catch (error) {
      console.error(error);
      toast.error("Failed to update status. Please try again", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    }
  };

  // ─── Download approved ─────────────────────────────────────────────────────
  const downloadApproved = async () => {
    if (fetchedImages.length === 0) {
      showErrorToast("No approved images available for download.");
      return;
    }
    setIsDownloading(true);
    try {
      const response = await apiService.get<ArrayBuffer>(
        `/api/individuals/deceased-gallery/${fetchedImages[0].deceased}/bulk-download/`,
      );
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "approved_images.zip";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error(error);
      showErrorToast("Failed to download approved images");
    } finally {
      setIsDownloading(false);
    }
  };

  // ─── Skeleton grid ───────────────────────────────────────────────────────────
  
  const skeletonGrid = (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr 1fr",
          sm: "repeat(3, 1fr)",
          md: "repeat(4, 1fr)",
          lg: "repeat(5, 1fr)",
        },
        gap: 2,
        mt: 2,
      }}
    >
      {[...Array(isMobile ? 4 : 8)].map((_, i) => (
        <Paper key={i} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Skeleton variant="rectangular" sx={{ width: "100%", aspectRatio: "4/3" }} />
          <Box sx={{ p: 1.5 }}>
            <Skeleton variant="text" width="70%" height={18} />
            <Skeleton variant="text" width="50%" height={16} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={32} sx={{ borderRadius: 1 }} />
          </Box>
        </Paper>
      ))}
    </Box>
  );

  return (
    <Box bgcolor={reduxTheme.primary_colors[900]} p={2} borderRadius={3}>
      
      {/* ─── Toolbar ─────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", lg: "center" },
          gap: 2,
          mt: 2,
        }}
      >
        {/* Left: title + filter + bulk */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            Images and Media
          </Typography>

          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-start" }}>
            <CustomSelect
              label=""
              defaultValue="all"
              sx={{ width: "120px" }}
              size="small"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </CustomSelect>

            <Box display="flex" gap={1} alignItems="center">
              <CustomSelect
                onChange={(e) => updateBulkAction(e)}
                label="Bulk Actions"
                defaultValue="approve"
                sx={{ width: "130px" }}
                size="small"
              >
                <MenuItem value="approve">Approve</MenuItem>
                <MenuItem value="spam">Spam</MenuItem>
                <MenuItem value="unapprove">Unapprove</MenuItem>
              </CustomSelect>
              <Button variant="contained" onClick={bulkAction}>
                Apply
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Right: download */}
          <Button
            variant="contained"
            onClick={downloadApproved}
            disabled={isDownloading}
          >
            {isDownloading ? <CircularProgress size={20} /> : "Download Approved"}
          </Button>
        </Box>

      {/* ─── Select All ──────────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, mt: 3 }}>
        <Checkbox checked={checkIsChecked()} onClick={toggleChecked} />
        <Typography variant="body1">Select All</Typography>
      </Box>

      {/* ─── States ──────────────────────────────────────────────────────────── */}
      {isLoading && skeletonGrid}

      {error && !isLoading && (
        <Box sx={{ textAlign: "center", pt: 4, pb: 4 }}>
          <Typography variant="body1" mt={1}>
            Failed to fetch images
          </Typography>
          <Button variant="contained" sx={{ mt: 2 }} onClick={fetchImages}>
            Retry
          </Button>
        </Box>
      )}

      {!error && !isLoading && renderingImages.length === 0 && (
        <Box sx={{ textAlign: "center", pt: 4, pb: 4 }}>
          <Typography variant="body1" mt={1}>
            No images available at the moment
          </Typography>
        </Box>
      )}

      {/* ─── Responsive image grid ───────────────────────────────────────────── */}
      {!isLoading && !error && renderingImages.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr 1fr",          // 2 cols on phone
              sm: "repeat(3, 1fr)",   // 3 cols on small tablet
              md: "repeat(4, 1fr)",   // 4 cols on tablet
              lg: "repeat(5, 1fr)",   // 5 cols on desktop
            },
            gap: { xs: 1.5, sm: 2 },
            mt: 2,
          }}
        >
          {renderingImages.map((image) => (
            <GalleryImage
              key={image.id}
              image={image}
              fetchImages={fetchImages}
              checkedImagesIDs={checkedImagesIDs}
              removeImageToChecked={removeImageToChecked}
              addImageToChecked={addImageToChecked}
              onRefreshCount={onRefreshCount}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// ─── GalleryImage card ─────────────────────────────────────────────────────────

interface GalleryImageProps {
  image: Image;
  checkedImagesIDs: number[];
  removeImageToChecked: (id: number) => void;
  addImageToChecked: (id: number) => void;
  fetchImages: () => Promise<void>;
  onRefreshCount?: () => void;
}

const GalleryImage: React.FC<GalleryImageProps> = ({
  image,
  fetchImages,
  checkedImagesIDs,
  removeImageToChecked,
  addImageToChecked,
  onRefreshCount,
}) => {
  const imageID = image.id;
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const formattedDate = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(image.created_at));

  const getStatusChipProps = (status: string) => {
    switch (status) {
      case "unapproved":
        return { label: "Pending", bgcolor: "#FEF3C7", color: "#D97706" };
      case "approved":
        return { label: "Approved", bgcolor: "#F0FDFA", color: "#0D9488" };
      case "spam":
        return { label: "Spam", bgcolor: "#FEE2E2", color: "#B91C1C" };
      default:
        return { label: "Unknown", bgcolor: "#BDBDBD40", color: "#BDBDBD" };
    }
  };

  const checkIfImageIsChecked = useCallback(() => {
    setIsChecked(checkedImagesIDs.some((id) => id === image.id));
  }, [checkedImagesIDs, image.id]);

  useEffect(() => {
    checkIfImageIsChecked();
  }, [checkIfImageIsChecked]);

  // ─── Single image action ──────────────────────────────────────────────────
  const singleImageAction = async (action: string) => {
    setIsBusy(true);
    try {
      await apiService.post(
        `/api/individuals/deceased-gallery/bulk-${action}/`,
        { files_ids: [imageID] },
      );
      await fetchImages();          // wait for refresh
      onRefreshCount?.();           // refresh badge count
      toast.success(`Image ${action}d.`, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } catch (error) {
      console.error(error);
      toast.error(`Failed to ${action} image. Please try again.`, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setIsBusy(false);
    }
  };

  // ─── Delete ────────────────────────────────────────────────────────────────
  const confirmDelete = async () => {
    setDeleteDialogOpen(false);
    setIsBusy(true);
    try {
      await apiService.delete(`/api/individuals/deceased-gallery/${imageID}/`);
      await fetchImages();          // wait for refresh
      onRefreshCount?.();           // refresh badge count
      toast.success("Image deleted.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete image. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setIsBusy(false);
    }
  };

  const handleCheckToggle = () => {
    if (isChecked) {
      removeImageToChecked(image.id);
    } else {
      addImageToChecked(image.id);
    }
  };

  const chipProps = getStatusChipProps(image.status);

  return (
    <Paper
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "box-shadow 0.15s ease",
        "&:active": { transform: "scale(0.98)" },
      }}
    >
      {/* Image area */}
      <Box
        sx={{
          width: "100%",
          aspectRatio: "4 / 3",
          overflow: "hidden",
          position: "relative",
          flexShrink: 0,
        }}
      >
        <Checkbox
          checked={isChecked}
          onClick={handleCheckToggle}
          size="small"
          sx={{
            width: 28,
            height: 28,
            backgroundColor: reduxTheme.primary_colors[900],
            borderRadius: "50%",
            position: "absolute",
            left: 5,
            top: 5,
            zIndex: 1,
          }}
        />
        <Chip
          label={chipProps.label}
          size="small"
          sx={{
            position: "absolute",
            top: 5,
            right: 5,
            zIndex: 1,
            backgroundColor: chipProps.bgcolor,
            color: chipProps.color,
            fontSize: "0.65rem",
            height: 20,
          }}
        />
        <img
          src={image.file}
          alt={image.title || "Uploaded"}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      </Box>

      {/* Info */}
      <Stack spacing={0.5} sx={{ p: 1, flexGrow: 1 }}>
        <Typography
          variant="caption"
          noWrap
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          <PersonOutlineIcon sx={{ fontSize: "0.85rem", opacity: 0.6 }} />
          {image.title}
        </Typography>
        <Typography
          variant="caption"
          sx={{ display: "flex", alignItems: "center", gap: 0.5, opacity: 0.7 }}
        >
          <CalendarTodayIcon sx={{ fontSize: "0.75rem", opacity: 0.6 }} />
          {formattedDate}
        </Typography>
      </Stack>

      {/* Actions */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 0.75,
          p: 1,
          pt: 0,
        }}
      >
        {isBusy ? (
          <Box sx={{ gridColumn: "1 / -1", display: "flex", justifyContent: "center", py: 0.5 }}>
            <CircularProgress size={18} />
          </Box>
        ) : (
          <>
            {image.status === "approved" ? (
              <Button
                variant="contained"
                size="small"
                sx={{ backgroundColor: "#B45309", fontSize: "0.65rem", px: 0.25, py: 0.5 }}
                onClick={() => singleImageAction("unapprove")}
              >
                Unapprove
              </Button>
            ) : (
              <Button
                variant="contained"
                size="small"
                sx={{ backgroundColor: "#16A34A", fontSize: "0.65rem", px: 0.25, py: 0.5 }}
                onClick={() => singleImageAction("approve")}
              >
                Approve
              </Button>
            )}
            
            <Button
              variant="contained"
              size="small"
              sx={{ backgroundColor: "#EA580C", fontSize: "0.65rem", px: 0.25, py: 0.5 }}
              onClick={() => singleImageAction("spam")}
            >
              Spam
            </Button>

            <Button
              variant="contained"
              size="small"
              sx={{ backgroundColor: "#E11D48", fontSize: "0.65rem", px: 0.25, py: 0.5 }}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete
            </Button>
          </>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this image? This action is permanent and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setDeleteDialogOpen(false)} sx={{ color: "text.secondary" }}>
            Cancel
          </MuiButton>
          <MuiButton variant="contained" color="error" onClick={confirmDelete}>
            Delete
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

// ─── PropTypes for GalleryImage ────────────────────────────────────────────────
GalleryImage.propTypes = {
  image: PropTypes.shape({
    id: PropTypes.number.isRequired,
    content_type: PropTypes.string.isRequired,
    created_at: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
    deceased: PropTypes.number.isRequired,
    description: PropTypes.string.isRequired,
    file: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    updated_at: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
    status: PropTypes.string.isRequired,
  }).isRequired,
  checkedImagesIDs: PropTypes.arrayOf(PropTypes.number).isRequired,
  removeImageToChecked: PropTypes.func.isRequired,
  addImageToChecked: PropTypes.func.isRequired,
  fetchImages: PropTypes.func.isRequired,
  onRefreshCount: PropTypes.func,
};